import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TaxonomyFieldType, TaxonomyListRef } from '@/entities/taxonomy';
import {
  taxonomyListDetailQueryOptions,
  taxonomyListsQueryOptions,
} from '@/entities/taxonomy-list';
import {
  defaultCodeColumnId,
  resolveBoundColumnPair,
  resolveBoundListVersion,
} from '@inference.engine/taxonomy/web';
import { bindableColumns } from '@/entities/taxonomy-list';
import { isBindableList } from './isBindableList';

/**
 * What saving should do to the field's binding.
 *
 * `unchanged` exists because callers merge patches with a spread: sending the key with a
 * rebuilt ref would unbind on a failed lookup, or quietly turn a publish pin into a user pin.
 */
export type ListBindingChange =
  | { kind: 'unchanged' }
  | { kind: 'unbind' }
  | { kind: 'bind'; listRef: TaxonomyListRef };

export interface UseListBindingOptions {
  open: boolean;
  type: TaxonomyFieldType;
  initialListRef?: TaxonomyListRef | null;
}

export function useListBinding({ open, type, initialListRef }: UseListBindingOptions) {
  const initial = initialListRef ?? null;
  const [listId, setListIdState] = useState(initial?.listId ?? '');
  /** `undefined` until the user picks, so the default can follow the chosen list. */
  const [codeChoice, setCodeChoice] = useState<string | null | undefined>(undefined);
  const [follow, setFollow] = useState(!initial?.listVersionId);

  useEffect(() => {
    if (!open) return;
    setListIdState(initial?.listId ?? '');
    setCodeChoice(undefined);
    setFollow(!initial?.listVersionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reseed on open, not on every parent render
  }, [open]);

  const setListId = (next: string) => {
    setListIdState(next);
    setCodeChoice(undefined);
    setFollow(next === initial?.listId ? !initial.listVersionId : true);
  };

  const listsQuery = useQuery(taxonomyListsQueryOptions());
  const detailQuery = useQuery({ ...taxonomyListDetailQueryOptions(listId), enabled: !!listId });

  const lists = useMemo(() => listsQuery.data ?? [], [listsQuery.data]);
  const bindableLists = useMemo(
    () => lists.filter((l) => isBindableList(l, initial?.listId)),
    [lists, initial?.listId],
  );
  const boundList = bindableLists.find((l) => l.id === listId);
  const keepsInitialList = !!initial && listId === initial.listId;

  const target = useMemo(
    () =>
      resolveBoundListVersion(detailQuery.data, {
        listVersionId: keepsInitialList ? initial.listVersionId : null,
      }),
    [detailQuery.data, keepsInitialList, initial?.listVersionId],
  );
  const latestPublished = useMemo(() => {
    const resolved = resolveBoundListVersion(detailQuery.data, { listVersionId: null });
    return resolved?.source === 'published' ? resolved.version : undefined;
  }, [detailQuery.data]);

  const columns = target?.version.columns ?? [];
  const pair = resolveBoundColumnPair(columns);

  const codeColumnId: string | null | undefined =
    codeChoice !== undefined
      ? codeChoice
      : keepsInitialList
        ? (initial.codeColumnId ?? null)
        : defaultCodeColumnId(columns, target?.version.rows);

  const pinnedVersion = follow
    ? undefined
    : keepsInitialList && initial.listVersionId
      ? target?.version
      : latestPublished;
  const canPin = !!latestPublished || (keepsInitialList && !!initial.listVersionId);

  const active = type === 'dropdown' && listId !== '';
  const unresolved = active && !pair;
  // Only a list the user just picked has to resolve before saving: for the field's own list,
  // `change()` leaves the stored binding alone, so other edits must stay possible.
  const blocksSave = unresolved && !keepsInitialList;

  const change = (): ListBindingChange => {
    if (!active) return initial ? { kind: 'unbind' } : { kind: 'unchanged' };
    if (!pair) return { kind: 'unchanged' };

    const listVersionId = follow ? null : (pinnedVersion?.id ?? null);
    const valueColumnId = keepsInitialList ? initial.valueColumnId : pair.valueColumn.id;
    const samePin = keepsInitialList && (initial.listVersionId ?? null) === listVersionId;
    const listRef: TaxonomyListRef = {
      listId,
      listVersionId,
      ...(samePin && initial.pinnedBy ? { pinnedBy: initial.pinnedBy } : {}),
      valueColumnId,
      codeColumnId: codeColumnId ?? null,
    };

    if (
      keepsInitialList &&
      samePin &&
      initial.valueColumnId === valueColumnId &&
      (initial.codeColumnId ?? null) === listRef.codeColumnId
    ) {
      return { kind: 'unchanged' };
    }
    return { kind: 'bind', listRef };
  };

  return {
    listId,
    setListId,
    codeColumnId: codeColumnId ?? null,
    setCodeColumnId: setCodeChoice,
    follow,
    setFollow,
    canPin,
    /** The version a pin would freeze to, for the explainer. */
    pinVersionNumber: (pinnedVersion ?? latestPublished ?? target?.version)?.versionNumber,
    /** What the code picker offers: Value and Code, plus a column the field already reads. */
    columns: bindableColumns(
      columns,
      keepsInitialList ? [initial.valueColumnId, initial.codeColumnId] : [],
    ),
    /** Every list, the ones that cannot bind included, so a picker can say why. */
    lists,
    bindableLists,
    boundList,
    listsPending: listsQuery.isPending,
    detailPending: !!listId && detailQuery.isPending,
    /** The chosen list has no usable column pair, so its code and follow controls cannot apply. */
    unresolved,
    blocksSave,
    change,
  };
}

export type ListBinding = ReturnType<typeof useListBinding>;
